/**
 * Grading scale constants - single source of truth for all grade verbiage.
 *
 * Keep this file in sync with the public /grading page and the /terms page.
 * When values change, update SCALE_EFFECTIVE_DATE and surface a notice on
 * order/invoice emails (admin-controlled via the `email.gradingChangeNoticeEnabled` setting).
 */

export const GRADE_DEFECT_CEILINGS = {
  A: 0,
  'A-': 0,
  'B+': 5,
  'B-': 9,
} as const;

export type GradeKey = keyof typeof GRADE_DEFECT_CEILINGS;

/**
 * Grades sold as aggregate quantity (no customer-visible serial selection).
 * A- must be treated exactly like A across cart merge, availability display,
 * and inventory reservation.
 */
export function isAggregateGrade(grade: string | null | undefined): boolean {
  return grade === 'A' || grade === 'A-';
}

/**
 * Canonicalize any raw supplier/legacy grade text to the display codes
 * A / A- / B+ / B / B- / C. Handles "CLASS A MINUS", "A MINUS", unicode
 * minus, B-COMPLETE/B-INCOMPLETE, etc. Unknown values pass through trimmed
 * so nothing is silently hidden. Safe to call on already-canonical values.
 * Use this at EVERY display boundary (badges, PDFs, tooltips, exports).
 */
export function normalizeGradeLabel(raw: string | null | undefined): string {
  const original = (raw ?? '').toString().trim();
  const c = original.toUpperCase().replace(/^CLASS\s+/, '').replace(/\u2212/g, '-').replace(/\s+/g, ' ').trim();
  if (c === 'A' || c === 'A COMPLETE') return 'A';
  if (c === 'A-' || c === 'A MINUS' || c === 'AMINUS' || c === 'A- COMPLETE' || c === 'A MINUS COMPLETE') return 'A-';
  if (c === 'B+' || c === 'B PLUS' || c === 'BPLUS' || c === 'B+ COMPLETE' || c === 'B PLUS COMPLETE' || c === 'B-COMPLETE' || c === 'B COMPLETE') return 'B+';
  if (c === 'B-' || c === 'B MINUS' || c === 'BMINUS' || c === 'B- INCOMPLETE' || c === 'B-INCOMPLETE' || c === 'B MINUS INCOMPLETE') return 'B-';
  if (c === 'B') return 'B';
  if (c === 'C') return 'C';
  return original;
}

export const SCALE_EFFECTIVE_DATE = '2026-04-26';
export const SCALE_EFFECTIVE_DATE_LABEL = 'April 26, 2026';


export const GRADE_DESCRIPTIONS: Record<GradeKey, {
  label: string;
  short: string;
  long: string;
  emoji: string;
}> = {
  A: {
    label: 'A Grade',
    short: 'No front-facing cosmetic flaws',
    long: 'Looks new — no visible blemishes on any front-facing surface.',
    emoji: '✅',
  },
  'A-': {
    label: 'A- Grade',
    short: 'Front professionally restored & wrapped',
    long: 'The front previously had dents or dings that have been professionally filled and covered with an automotive-grade vinyl wrap — the result looks stunning. No current front-facing blemishes. Fully functional with the factory warranty.',
    emoji: '✨',
  },
  'B+': {
    label: 'B+ Grade',
    short: `Up to ${GRADE_DEFECT_CEILINGS['B+']} small front-facing blemishes`,
    long: `Minor cosmetic — up to ${GRADE_DEFECT_CEILINGS['B+']} small blemishes on front-facing surfaces. Fully functional.`,
    emoji: '⚪',
  },
  'B-': {
    label: 'B- Grade',
    short: `Up to ${GRADE_DEFECT_CEILINGS['B-']} front-facing blemishes`,
    long: `Noticeable wear — up to ${GRADE_DEFECT_CEILINGS['B-']} blemishes on front-facing surfaces. Fully functional.`,
    emoji: '⚫',
  },
};

export const GRADE_LEGEND_LINE = `A = no front-facing defects · A- = front restored & vinyl-wrapped · B+ = up to ${GRADE_DEFECT_CEILINGS['B+']} · B- = up to ${GRADE_DEFECT_CEILINGS['B-']}`;

export const GRADING_TOOLTIP = `Refrigerator grades reflect front-facing cosmetic defects only — every unit is fully functional and ships with the factory warranty. A = no defects, A- = front previously dented, professionally filled and covered with an automotive-grade vinyl wrap, B+ = up to ${GRADE_DEFECT_CEILINGS['B+']}, B- = up to ${GRADE_DEFECT_CEILINGS['B-']}.`;

/**
 * Default per-grade detail line shown inside the grade-pill tooltip on the
 * storefront product detail page. Returns `null` when the grade isn't one
 * of the canonical A / B+ / B- buckets.
 */
export function defaultGradeDetailFor(grade: string): string | null {
  const normalized = grade.toUpperCase().trim();
  if (normalized === 'A-' || normalized === 'CLASS A MINUS') {
    return 'A− — front previously dented, professionally filled and covered with an automotive-grade vinyl wrap. Looks stunning, fully functional.';
  }
  if (normalized === 'A' || normalized === 'CLASS A') {
    return 'A — no front-facing cosmetic defects.';
  }
  if (normalized === 'B+' || normalized === 'CLASS B PLUS' || normalized === 'B-COMPLETE') {
    return `B+ — up to ${GRADE_DEFECT_CEILINGS['B+']} small front-facing blemishes. Fully functional.`;
  }
  if (
    normalized === 'B' ||
    normalized === 'B-' ||
    normalized === 'CLASS B MINUS' ||
    normalized === 'B-INCOMPLETE'
  ) {
    return `B− — up to ${GRADE_DEFECT_CEILINGS['B-']} front-facing blemishes. Fully functional.`;
  }
  return null;
}

/**
 * Resolved grading copy for the storefront grade pill. Server-side
 * resolution so the API is the single source of truth: stored admin
 * overrides win, otherwise the canonical refrigerator defaults from this
 * module are returned. Pallet categories also surface a pill label.
 *
 * Task #156.
 */
export interface ResolvedGradingCopy {
  tooltip: string;
  detail: { A: string; 'A-': string; 'B+': string; 'B-': string };
  pillLabel: string;
}

/**
 * Build the resolved grading copy for a category. Accepts the raw
 * (nullable) admin-edited fields and falls back to the refrigerator
 * defaults so callers never have to repeat the fallback logic.
 */
export function resolveGradingCopy(input: {
  gradingTooltip?: string | null;
  gradeDetailLines?: Record<string, string> | null;
  inventoryPillLabel?: string | null;
}): ResolvedGradingCopy {
  const lines = input.gradeDetailLines ?? null;
  const pickDetail = (g: 'A' | 'A-' | 'B+' | 'B-'): string => {
    if (lines && typeof lines === 'object') {
      const candidates = [g, g.replace('-', '−'), g.replace('−', '-')];
      for (const k of candidates) {
        const v = lines[k];
        if (typeof v === 'string' && v.trim().length > 0) return v;
      }
    }
    return defaultGradeDetailFor(g) ?? '';
  };
  return {
    tooltip: (input.gradingTooltip && input.gradingTooltip.trim()) || GRADING_TOOLTIP,
    detail: { A: pickDetail('A'), 'A-': pickDetail('A-'), 'B+': pickDetail('B+'), 'B-': pickDetail('B-') },
    pillLabel: (input.inventoryPillLabel && input.inventoryPillLabel.trim()) || 'Pallet',
  };
}
